<template>
  <div>
    <v-card class="px-1 px-sm-10 px-md-15 py-15 card" elevation="2">
      <base-avatar
        class="avatar"
        :name="user.full_name"
        :photo="user.photo"
        size="100"
      />
      <p class="mt-2 mb-5 text-h4 text-center font-weight-medium">
        {{ user.full_name }}
      </p>
      <v-row class="mb-4" dense>
        <v-col
          v-for="item in analytics"
          :key="item.title"
          cols="12"
          lg="3"
          sm="6"
        >
          <analytics-card
            black-shadow
            :body="item.body"
            :color="item.color"
            :loading="loadingAnalytics"
            :title="item.title"
          />
        </v-col>
      </v-row>
      <v-row v-if="!loadingAssignmentsData" dense>
        <v-col cols="0" md="9" />
        <v-col cols="0" md="3">
          <base-select
            v-model="status"
            clearable
            :items="statusChoices"
            outlined
            placeholder="Status"
          />
        </v-col>
      </v-row>
      <div class="mt-5">
        <div v-if="loadingAssignmentsData" class="d-flex justify-center">
          <v-progress-circular
            class="mt-10"
            color="primary"
            indeterminate
            size="50"
          />
        </div>
        <div
          v-else-if="courses.length + qualifications.length === 0"
          class="d-flex justify-center"
        >
          <p class="text-h6">
            Looks like there are no assignments to review.
          </p>
        </div>
        <v-expansion-panels v-else class="expansion-panel">
          <div
            v-for="(qualification, i) in qualifications"
            :key="i"
            class="expansion-panel-wrapper"
          >
            <qualification-panel
              :qualification="qualification"
              @get-courses="getCourses"
            />
          </div>
          <div
            v-for="(course, i) in courses"
            :key="i"
            class="expansion-panel-wrapper"
          >
            <course-panel
              :course="course"
              is-stand-alone
              @get-courses="getCourses"
            />
          </div>
        </v-expansion-panels>
      </div>
    </v-card>
  </div>
</template>

<script>
import AnalyticsCard from '@/components/_shared/AnalyticsCard'
import QualificationPanel from '@/components/user_assignments/QualificationPanel'
import CoursePanel from '@/components/user_assignments/CoursePanel.vue'

import { EVALUATION_STATUS, USER_COURSE_QA } from '@/utils/constants'
import filters from '@/utils/mixins/filters'

export default {
  name: 'UserAssignments',

  components: {
    AnalyticsCard,
    QualificationPanel,
    CoursePanel,
  },

  mixins: [filters],

  data: () => ({
    user: {},

    status: USER_COURSE_QA.NOT_STARTED,
    statusChoices: [
      { text: 'Pending', value: EVALUATION_STATUS.UNDER_EVALUATION },
      { text: 'Challenged', value: EVALUATION_STATUS.CHALLENGED },
      {
        text: "Awaiting Professor's Response",
        value: EVALUATION_STATUS.CHANGES_REQUESTED,
      },
      {
        text: 'Quality Assurance Complete',
        value: EVALUATION_STATUS.COMPLETED,
      },
    ],

    analytics: [],
    loadingAnalytics: false,

    loadingAssignmentsData: false,

    courses: [],
    qualifications: [],
  }),

  computed: {
    userId() {
      return this.$route.params.userId
    },
  },

  watch: {
    status() {
      this.getCourses()
    },
  },

  mounted() {
    this.getAnalytics()
    this.getCourses()
  },

  methods: {
    async getCourses() {
      try {
        this.loadingAssignmentsData = true
        const res = await this.$api.assignments.retrieveCourseAssignments({
          userId: this.userId,
        })
        const data = res.data.data

        const getSortedAssignmentsByCourse = courseUUID => {
          return data.assignments
            .filter(
              item =>
                item.courses.filter(course => course === courseUUID).length > 0
            )
            .sort((a, b) => (a.shortTitle > b.shortTitle ? 1 : -1))
        }

        const mapAssignmentsToCourses = courses => {
          return courses
            ? courses.map(course => ({
                ...course,
                assignments: getSortedAssignmentsByCourse(course.uuid),
              }))
            : []
        }

        const standAloneCourses = mapAssignmentsToCourses(
          data.enrollments.courses
        )

        const qualifications = data.enrollments.qualifications
          ? data.enrollments.qualifications.map(qualification => ({
              ...qualification,
              courses: mapAssignmentsToCourses(qualification.courses),
            }))
          : []

        const certifications = data.enrollments.certifications
          ? data.enrollments.certifications.map(certification => ({
              ...certification,
              courses: mapAssignmentsToCourses(certification.courses),
            }))
          : []

        this.qualifications = [...qualifications, ...certifications]
        this.courses = standAloneCourses

        console.log(this.qualifications)
      } catch (err) {
        console.error(err)
      } finally {
        this.loadingAssignmentsData = false
      }
    },

    async getAnalytics() {
      try {
        const res = await this.$api.assignments.userIqaOverview(this.userId)
        const analytics = res.data.data
        this.user = analytics.user
        this.analytics = [
          {
            body: `${analytics.evaluations_completed}/${analytics.total_evaluations}`,
            color: 'green',
            title: 'Evaluations completed',
          },
          {
            body: `${analytics.pending_evaluations}/${analytics.total_evaluations}`,
            color: 'yellow',
            title: 'Pending Evaluations',
          },
          {
            body: `${analytics.awaiting_response}/${analytics.total_evaluations}`,
            color: 'orange',
            title: 'Awaiting Response',
          },
          {
            body: `${analytics.challenged_evaluations}/${analytics.total_evaluations}`,
            color: 'red',
            title: 'Challenged Evaluations',
          },
        ]
        this.loadingAnalytics = false
      } catch (err) {
        console.error(err)
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.card {
  position: relative;

  .avatar {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .expansion-panel-wrapper {
    width: 100%;
  }
}
</style>
